import { ISMEDesignGuidelinesExtended } from './SMEDesignGuidelineTypes';
import { ISMEDesignGuidelineFilter, newSMEDesignGuidelineFilter } from './SMEDesignGuidelineFilter';
import logger from '../../../Services/LoggingService';

/**
 * Factory function to create modal handlers with proper closure scope
 */
export const createModalHandlers = (
  setFormStateFromItem: (item?: ISMEDesignGuidelinesExtended) => void,
  setModalTitle: (title: string) => void,
  setIsDisabled: (disabled: boolean) => void,
  setIsModalOpen: (open: boolean) => void
) => {
  const showModal = (item?: ISMEDesignGuidelinesExtended) => {
    setFormStateFromItem(item);
    setModalTitle(item === undefined ? "New Design Guideline" : "Edit Design Guideline");
    setIsDisabled(item !== undefined);
    setIsModalOpen(true);
  };

  const hideModal = () => {
    setIsModalOpen(false);
  };

  return {
    showModal,
    hideModal
  };
};

/**
 * Factory function to create sorting handlers
 */
export const createSortingHandlers = (
  dataColumns: any[],
  order: 'asc' | 'desc',
  orderBy: keyof ISMEDesignGuidelinesExtended | '',
  setOrder: (order: 'asc' | 'desc') => void,
  setOrderBy: (orderBy: keyof ISMEDesignGuidelinesExtended | '') => void
) => {
  const handleRequestSort = (property: keyof ISMEDesignGuidelinesExtended) => {
    const column = dataColumns.find(col => col.id === property);
    if (!column || column.sortable === false) return;
    
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return {
    handleRequestSort
  };
};

/**
 * Factory function to create filter handlers
 */
export const createFilterHandlers = (
  showFilters: boolean,
  isInitialApiCallComplete: boolean,
  currentData: ISMEDesignGuidelinesExtended[],
  setShowFilters: (show: boolean) => void,
  setDesignGuidelineFilter: (filter: ISMEDesignGuidelineFilter) => void,
  setIsFiltering: (filtering: boolean) => void,
  setFilteredData: (data: ISMEDesignGuidelinesExtended[]) => void
) => {
  const toggleShowFilters = () => {
    setShowFilters(!showFilters);
  };

  const resetFilters = () => {
    const defaultFilter = newSMEDesignGuidelineFilter();
    setDesignGuidelineFilter(defaultFilter);
    setIsFiltering(false);
    
    // Use current data rather than making a new API call
    if (isInitialApiCallComplete) {
      setFilteredData(currentData);
    }
  };

  const handleFilterChange = (filter: ISMEDesignGuidelineFilter, isFiltering: boolean) => {
    setDesignGuidelineFilter(filter);
    setIsFiltering(isFiltering);
  };

  return {
    toggleShowFilters,
    resetFilters,
    handleFilterChange
  };
};

/**
 * Factory function to create form handlers
 */
export const createFormHandlers = (
  setSelectedIndustry: (value: string) => void,
  setSelectedSubIndustry: (value: string) => void,
  setSelectedApplication: (value: string) => void,
  setSelectedSubApplication: (value: string) => void,
  setSelectedProductLine: (value: string) => void,
  setSelectedPosition: (value: string) => void,
  setSelectedProcessStage: (value: string) => void,
  setDesignSuggestion: (value: string) => void,
  addNewDesignGuideline: () => Promise<void>
) => {
  const handleSelectChange = (event: any, field: string) => {
    const value = event.target.value;
    
    switch (field) {
      case 'industry':
        setSelectedIndustry(value);
        break;
      case 'subIndustry':
        setSelectedSubIndustry(value);
        break;
      case 'application':
        setSelectedApplication(value);
        break;
      case 'subApplication':
        setSelectedSubApplication(value);
        break;
      case 'productLine':
        setSelectedProductLine(value);
        break;
      case 'position':
        setSelectedPosition(value);
        break;
      case 'processStage':
        setSelectedProcessStage(value);
        break;
      default:
        break;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addNewDesignGuideline();
  };

  const handleDesignSuggestionChange = (value: string) => {
    setDesignSuggestion(value);
  };

  return {
    handleSelectChange,
    handleSubmit,
    handleDesignSuggestionChange
  };
};

/**
 * Factory function to create data processing handlers
 */
export const createDataProcessingHandlers = (
  logPerformance: (label: string, startTime: number) => void
) => {
  const filterDesignGuidelines = (
    data: ISMEDesignGuidelinesExtended[], 
    designGuidelineFilter: ISMEDesignGuidelineFilter
  ) => {
    const startTime = performance.now();
    if (!data || data.length === 0) {
      return [];
    }
    
    logger.debug('Filtering guidelines', { 
      totalCount: data.length,
      filter: designGuidelineFilter 
    }, {
      component: 'SMEDesignGuideline',
      method: 'filterDesignGuidelines'
    });
    
    const filtered = data.filter(item => {
      if (designGuidelineFilter.industry && item.industry !== designGuidelineFilter.industry) return false;
      if (designGuidelineFilter.subIndustry && item.subIndustry !== designGuidelineFilter.subIndustry) return false;
      if (designGuidelineFilter.application && item.application !== designGuidelineFilter.application) return false;
      if (designGuidelineFilter.subApplication && item.subApplication !== designGuidelineFilter.subApplication) return false;
      if (designGuidelineFilter.processStage && item.processStage !== designGuidelineFilter.processStage) return false;
      if (designGuidelineFilter.productLine && item.productLine !== designGuidelineFilter.productLine) return false;
      if (designGuidelineFilter.alfaLavalPosition && item.alfaLavalPosition !== designGuidelineFilter.alfaLavalPosition) return false;
      return true;
    });
    
    const duration = performance.now() - startTime;
    logger.debug('Filtering complete', { 
      filteredCount: filtered.length,
      duration: `${duration.toFixed(2)}ms`
    }, {
      component: 'SMEDesignGuideline',
      method: 'filterDesignGuidelines'
    });
    
    logPerformance('Filter guidelines', startTime);
    return filtered;
  };

  const sortData = (
    data: ISMEDesignGuidelinesExtended[], 
    order: 'asc' | 'desc', 
    orderBy: keyof ISMEDesignGuidelinesExtended | ''
  ): ISMEDesignGuidelinesExtended[] => {
    const startTime = performance.now();
    if (!orderBy || !data || data.length === 0) {
      return data;
    }
    
    const result = [...data].sort((a, b) => {
      const aValue = a[orderBy as keyof ISMEDesignGuidelinesExtended];
      const bValue = b[orderBy as keyof ISMEDesignGuidelinesExtended];
      
      // Handle null or undefined values
      if (aValue === null || aValue === undefined) return order === 'asc' ? -1 : 1;
      if (bValue === null || bValue === undefined) return order === 'asc' ? 1 : -1;
      
      // Handle date values
      if (orderBy === 'created') {
        const aDate = new Date(aValue as Date);
        const bDate = new Date(bValue as Date);
        return order === 'asc'
          ? aDate.getTime() - bDate.getTime()
          : bDate.getTime() - aDate.getTime();
      }
      
      // Handle string values
      const aString = String(aValue);
      const bString = String(bValue);
      
      return order === 'asc'
        ? aString.localeCompare(bString)
        : bString.localeCompare(aString);
    });
    
    const duration = performance.now() - startTime;
    logger.debug('Data sorted', {
      itemCount: result.length,
      orderBy,
      order,
      duration: `${duration.toFixed(2)}ms`
    }, {
      component: 'SMEDesignGuideline',
      method: 'sortData'
    });
    
    logPerformance('Sort data', startTime);
    return result;
  };

  return {
    filterDesignGuidelines,
    sortData
  };
}; 